import React, { useEffect, useState } from 'react';

const MultiplayerQuestionScreen = ({ 
  currentQuestion, 
  currentQuestionIndex, 
  gameData, 
  score, 
  selectedAnswer, 
  showFeedback, 
  feedback, 
  loading, 
  error, 
  submitAnswer, 
  getAnswerButtonClass,
  players,
  playerName,
  onRefreshLobby // Add this prop for refreshing lobby status
}) => {
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Log component lifecycle and state changes
  useEffect(() => {
    console.log('[MultiplayerQuestionScreen] Component mounted/updated', {
      currentQuestionIndex,
      hasQuestion: !!currentQuestion,
      questionText: currentQuestion?.question?.substring(0, 50) + '...',
      answersCount: currentQuestion?.answers?.length,
      score,
      selectedAnswer,
      showFeedback,
      loading,
      hasError: !!error,
      playersCount: players?.length || 0,
      playerName
    });
  }, [currentQuestion, currentQuestionIndex, gameData, score, selectedAnswer, showFeedback, loading, error, players, playerName]);

  // Set up auto-refresh for live scores during multiplayer game
  useEffect(() => {
    if (onRefreshLobby) {
      console.log('[MultiplayerQuestionScreen] Setting up auto-refresh for live scores');
      
      // Refresh immediately
      onRefreshLobby();
      
      // Set up interval to refresh every 2 seconds for live scores
      const interval = setInterval(() => {
        console.log('[MultiplayerQuestionScreen] Auto-refreshing for live scores');
        onRefreshLobby();
      }, 2000);
      
      setRefreshInterval(interval);
      
      // Cleanup interval on unmount
      return () => {
        console.log('[MultiplayerQuestionScreen] Cleaning up refresh interval');
        clearInterval(interval);
      };
    }
  }, [onRefreshLobby]);

  const handleAnswerClick = (answerIndex) => {
    console.log('[MultiplayerQuestionScreen] Answer clicked', {
      answerIndex,
      answerText: currentQuestion?.answers?.[answerIndex],
      questionIndex: currentQuestionIndex,
      selectedAnswer,
      showFeedback,
      playerName
    });
    submitAnswer(answerIndex, playerName);
  };

  const categories = [
    { id: 9, name: "General Knowledge" },
    { id: 17, name: "Science & Nature" },
    { id: 18, name: "Science: Computers" },
    { id: 19, name: "Science: Mathematics" },
    { id: 20, name: "Mythology" },
    { id: 21, name: "Sports" },
    { id: 22, name: "Geography" },
    { id: 23, name: "History" },
    { id: 24, name: "Politics" },
    { id: 25, name: "Art" },
    { id: 26, name: "Celebrities" },
    { id: 27, name: "Animals" },
    { id: 28, name: "Vehicles" }
  ];

  // Sort players by score (descending)
  const sortedPlayers = [...(players || [])].sort((a, b) => b.score - a.score);

  return (
    <div className={`question-card multiplayer ${loading ? 'loading' : ''}`}>
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ 
            width: `${((currentQuestionIndex + 1) / gameData.questionCount) * 100}%`,
            transition: 'width 0.3s ease-in-out'
          }}
        />
      </div>
      
      <div className="question-header">
        <div className="question-info">
          <div className="category-badge">
            {categories.find(cat => cat.id === gameData.category)?.name || 'General Knowledge'}
          </div>
          <div className="score-display">
            Question {currentQuestionIndex + 1} of {gameData.questionCount}
          </div>
          <div className={`difficulty-badge ${gameData.difficulty || 'medium'}`}>
            {(gameData.difficulty || 'medium').toUpperCase()}
          </div>
        </div>
        
        <div className="multiplayer-scores">
          <h4>Live Scores</h4>
          <div className="scores-list">
            {sortedPlayers.map((player, index) => (
              <div 
                key={player.userId || index} 
                className={`score-item ${player.playerName === playerName ? 'current-player' : ''}`}
              >
                <span className="player-rank">#{index + 1}</span>
                <span className="player-name">{player.playerName}</span>
                <span className="player-score">{player.score}</span>
                {player.completed && <span className="completed-badge">✓</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading question...
        </div>
      ) : currentQuestion ? (
        <div>
          <div className="question-text">{currentQuestion.question}</div>
          {currentQuestion.answers.map((answer, index) => (
            <button
              key={index}
              className={`answer-button ${getAnswerButtonClass(index)}`}
              onClick={() => handleAnswerClick(index)}
              disabled={selectedAnswer !== null || showFeedback}
            >
              {answer}
            </button>
          ))}
        </div>
      ) : (
        <div className="loading">
          <div className="loading-spinner"></div>
          No question available
        </div>
      )}
      
      {error && <div className="error">{error}</div>}
    </div>
  );
};

export default MultiplayerQuestionScreen;
